import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { SheetData, SheetRow, SheetColumn } from "./models";
import { FormulaEngine } from "./formula";

interface GridState {
    sheets: SheetData[];
    activeSheetIdx: number;
}

const initialState: GridState = {
    sheets: [
        {
            name: "Sheet 1",
            columns: [],
            rows: [],
        },
    ],
    activeSheetIdx: 0,
};

const formulaEngine = new FormulaEngine();

const updateSheet = (
    state: GridState,
    updater: (sheet: SheetData) => SheetData,
) => {
    state.sheets[state.activeSheetIdx] = updater(
        state.sheets[state.activeSheetIdx],
    );
};

export const gridSlice = createSlice({
    name: "grid",
    initialState,
    reducers: {
        initSheets: (state, action: PayloadAction<SheetData[]>) => {
            state.sheets = action.payload.map((sheet) => ({
                ...sheet,
                rows: formulaEngine.evaluateSheet(sheet),
            }));
        },
        addRow: (
            state,
            action: PayloadAction<{ row: SheetRow; index?: number }>,
        ) => {
            updateSheet(state, (sheet) => {
                const newRows = [...sheet.rows];
                if (action.payload.index !== undefined) {
                    newRows.splice(action.payload.index, 0, action.payload.row);
                } else {
                    newRows.push(action.payload.row);
                }
                const updatedSheet = { ...sheet, rows: newRows };
                return {
                    ...updatedSheet,
                    rows: formulaEngine.evaluateSheet(updatedSheet),
                };
            });
        },
        deleteRow: (state, action: PayloadAction<string>) => {
            updateSheet(state, (sheet) => {
                const updatedSheet = {
                    ...sheet,
                    rows: sheet.rows.filter((r) => r.rowId !== action.payload),
                };
                return {
                    ...updatedSheet,
                    rows: formulaEngine.evaluateSheet(updatedSheet),
                };
            });
        },
        toggleRowExpanded: (state, action: PayloadAction<string>) => {
            updateSheet(state, (sheet) => {
                return {
                    ...sheet,
                    rows: sheet.rows.map((r) => {
                        if (r.rowId === action.payload) {
                            const newExpanded = !r.isExpanded;
                            const newCells = r.cells.map((c) =>
                                c.type === "chevron"
                                    ? { ...c, isExpanded: newExpanded }
                                    : c,
                            );
                            return {
                                ...r,
                                isExpanded: newExpanded,
                                cells: newCells,
                            };
                        }
                        return r;
                    }),
                };
            });
        },
        setRowExpanded: (
            state,
            action: PayloadAction<{ rowId: string; isExpanded: boolean }>,
        ) => {
            updateSheet(state, (sheet) => {
                return {
                    ...sheet,
                    rows: sheet.rows.map((r) => {
                        if (r.rowId === action.payload.rowId) {
                            const newCells = r.cells.map((c) =>
                                c.type === "chevron"
                                    ? {
                                          ...c,
                                          isExpanded: action.payload.isExpanded,
                                      }
                                    : c,
                            );
                            return {
                                ...r,
                                isExpanded: action.payload.isExpanded,
                                cells: newCells,
                            };
                        }
                        return r;
                    }),
                };
            });
        },
        setAllRowsExpanded: (state, action: PayloadAction<boolean>) => {
            updateSheet(state, (sheet) => {
                return {
                    ...sheet,
                    rows: sheet.rows.map((r) => {
                        if (r.cells) {
                            const newCells = r.cells.map((c) =>
                                c.type === "chevron"
                                    ? { ...c, isExpanded: action.payload }
                                    : c,
                            );
                            return {
                                ...r,
                                isExpanded: action.payload,
                                cells: newCells,
                            };
                        }
                        return r;
                    }),
                };
            });
        },
        addColumn: (
            state,
            action: PayloadAction<{ column: SheetColumn; index?: number }>,
        ) => {
            updateSheet(state, (sheet) => {
                const newCols = [...sheet.columns];
                if (action.payload.index !== undefined) {
                    newCols.splice(
                        action.payload.index,
                        0,
                        action.payload.column,
                    );
                } else {
                    newCols.push(action.payload.column);
                }
                return { ...sheet, columns: newCols };
            });
        },
        resizeColumn: (
            state,
            action: PayloadAction<{ columnId: string; width: number }>,
        ) => {
            updateSheet(state, (sheet) => {
                return {
                    ...sheet,
                    columns: sheet.columns.map((c) =>
                        c.columnId === action.payload.columnId
                            ? { ...c, width: action.payload.width }
                            : c,
                    ),
                };
            });
        },
        updateCells: (state, action: PayloadAction<any[]>) => {
            updateSheet(state, (sheet) => {
                const newRows = [...sheet.rows];
                action.payload.forEach((change) => {
                    const rowIdx = newRows.findIndex(
                        (r) => r.rowId === change.rowId,
                    );
                    if (rowIdx === -1) return;

                    const colIdx = sheet.columns.findIndex(
                        (c) => c.columnId === change.columnId,
                    );
                    if (colIdx === -1) return;

                    const newRow = {
                        ...newRows[rowIdx],
                        cells: [...newRows[rowIdx].cells],
                    };
                    newRow.cells[colIdx] = change.newCell;

                    if (
                        change.newCell.type === "chevron" &&
                        change.newCell.isExpanded !== undefined
                    ) {
                        newRow.isExpanded = change.newCell.isExpanded;
                    }

                    newRows[rowIdx] = newRow;
                });

                const updatedSheet = { ...sheet, rows: newRows };
                return {
                    ...updatedSheet,
                    rows: formulaEngine.evaluateSheet(updatedSheet),
                };
            });
        },
        updateRows: (state, action: PayloadAction<any[]>) => {
            updateSheet(state, (sheet) => {
                const newRows = [...sheet.rows];
                action.payload.forEach((updatedRow: any) => {
                    const idx = newRows.findIndex(
                        (r) => r.rowId === updatedRow.rowId,
                    );
                    if (idx !== -1) {
                        newRows[idx] = {
                            ...updatedRow,
                            isExpanded: newRows[idx].isExpanded,
                        };
                    }
                });
                const updatedSheet = { ...sheet, rows: newRows };
                return {
                    ...updatedSheet,
                    rows: formulaEngine.evaluateSheet(updatedSheet),
                };
            });
        },
        syncSheetData: (state, action: PayloadAction<{ sheet: SheetData }>) => {
            updateSheet(state, (sheet) => {
                const preservedRows = action.payload.sheet.rows.map(
                    (incomingRow: any) => {
                        const existingRow = sheet.rows.find(
                            (r) => r.rowId === incomingRow.rowId,
                        );

                        const hasPreservedExpanded =
                            existingRow && existingRow.isExpanded !== undefined;
                        const finalRowExpanded = hasPreservedExpanded
                            ? existingRow.isExpanded
                            : incomingRow.isExpanded;

                        const cells = incomingRow.cells.map((c: any) => {
                            if (c.type === "chevron") {
                                const finalCellExpanded = hasPreservedExpanded
                                    ? existingRow.isExpanded
                                    : c.isExpanded;
                                return {
                                    ...c,
                                    isExpanded: finalCellExpanded ?? false,
                                };
                            }
                            return c;
                        });

                        return {
                            ...incomingRow,
                            isExpanded: finalRowExpanded,
                            cells,
                        };
                    },
                );
                const updatedSheet = {
                    ...action.payload.sheet,
                    rows: preservedRows,
                };
                return {
                    ...updatedSheet,
                    rows: formulaEngine.evaluateSheet(updatedSheet),
                };
            });
        },
    },
});

export const {
    initSheets,
    addRow,
    deleteRow,
    toggleRowExpanded,
    setRowExpanded,
    setAllRowsExpanded,
    addColumn,
    resizeColumn,
    updateCells,
    updateRows,
    syncSheetData,
} = gridSlice.actions;

export const selectActiveSheet = (state: any) => {
    if (!state.grid?.sheets || state.grid.sheets.length === 0) {
        return null;
    }
    return state.grid.sheets[state.grid.activeSheetIdx];
};

export default gridSlice.reducer;
